import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { Save, ArrowLeft, Plus, ChevronRight, List, Search, Shield, Zap, Info, Tag, Paperclip, History, AlertTriangle, Trash2, FlaskConical } from 'lucide-react';

// المودالات اللي إنت بتستخدمها في الـ PO/PR
import UniversalLookup from './UniversalLookup';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';

const SafetyPlanDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';
  
  const [activeTab, setActiveTab] = useState('Plan');
  const [selectedHazardIdx, setSelectedHazardIdx] = useState(null);
  const [selectedLotoIdx, setSelectedLotoIdx] = useState(null);
  const [selectedHazmatIdx, setSelectedHazmatIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [count, setCount] = useState(0);

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const [data, setData] = useState({
    safetypnum: '', description: '', is_active: true, status: 'DRAFT',
    siteid: session.site_id || '', orgid: session.org_id || '',
    hazards: [],
    tagouts: [],
    hazmat: [],
    work_assets: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {}, targetIdx: null, subIdx: null });

  // 1. جلب البيانات الأساسية مع تأمين المصفوفات
  useEffect(() => {
    if (!isNew) {
      axios.get(`/api/safety-plans/${id}`).then(res => {
        const resData = res.data;
        resData.hazards = resData.hazards || [];
        resData.tagouts = resData.tagouts || [];
        resData.hazmat = resData.hazmat || [];
        resData.work_assets = resData.work_assets || [];
        setData(resData);
      });
    }
  }, [id, isNew]);

  // 2. جلب عدد المرفقات
  const fetchCount = useCallback(async () => {
    if (!data.safetypnum || isNew) { setCount(0); return; }
    try {
      const res = await axios.get(`/api/attachments/count/SAFETYPLAN/${data.safetypnum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error(err); }
  }, [data.safetypnum, isNew]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  // --- 🚀 دوال الإضافة (Add Functions) ---
    const addHazard = () => {
        const newHazard = { hazardnum: '', description: '', priority: 'MEDIUM', precautions: [] };
        setData({ ...data, hazards: [...data.hazards, newHazard] });
        setSelectedHazardIdx(data.hazards.length);
    };

    const addPrecaution = (hIdx) => {
        const newHazards = [...data.hazards];
        newHazards[hIdx].precautions = newHazards[hIdx].precautions || [];
        newHazards[hIdx].precautions.push({ precautionnum: '', description: '' });
        setData({ ...data, hazards: newHazards });
    };

    const addLotoStep = () => {
        const newStep = { tagoutnum: '', sequence: data.tagouts.length + 1, description: '', required_state: '' };
        setData({ ...data, tagouts: [...data.tagouts, newStep] });
        setSelectedLotoIdx(data.tagouts.length);
    };

    const addHazmatRow = () => {
        const newMat = { itemnum: '', description: '', health_rating: '' };
        setData({ ...data, hazmat: [...data.hazmat, newMat] });
        setSelectedHazmatIdx(data.hazmat.length);
    };
    // 1. دالة إضافة سطر جديد للأصول (أو نفتح اللوك اب مباشرة)
    const addWorkAsset = () => {
        // بنفتح اللوك اب ونعرفه إننا عاوزين نربط في جدول الـ work_assets
        setLookup({
            isOpen: true, 
            title: 'Select Asset or Location', 
            type: 'assets', // ممكن تخليها assets_locations لو عامل كويري مشترك
            field: 'work_asset_row', 
            targetIdx: data.work_assets?.length || 0 
        });
    };


    // --- 🗑️ دوال الحذف المؤمنة (Delete Functions) ---
    const deleteHazard = (idx) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "This will remove the hazard and all its precautions!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3c70a4',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                const newHazards = data.hazards.filter((_, i) => i !== idx);
                setData({ ...data, hazards: newHazards });
                setSelectedHazardIdx(null);
                toast.success("Hazard removed");
            }
        });
    };

    const deletePrecaution = (hIdx, pIdx) => {
        const newHazards = [...data.hazards];
        newHazards[hIdx].precautions = newHazards[hIdx].precautions.filter((_, i) => i !== pIdx);
        setData({ ...data, hazards: newHazards });
        toast.success("Precaution removed");
    };

    const deleteLotoStep = (idx) => {
        Swal.fire({
            title: 'Delete Step?',
            text: "Do you want to remove this isolation step?",
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Delete'
        }).then((result) => {
            if (result.isConfirmed) {
                const newLoto = data.tagouts.filter((_, i) => i !== idx);
                const reorderedLoto = newLoto.map((item, i) => ({ ...item, sequence: i + 1 }));
                setData({ ...data, tagouts: reorderedLoto });
                setSelectedLotoIdx(null);
                toast.success("LOTO step removed");
            }
        });
    }; 
    
    const deleteHazmat = (idx) => {
        const newHazmat = data.hazmat.filter((_, i) => i !== idx);
        setData({ ...data, hazmat: newHazmat });
        setSelectedHazmatIdx(null);
        toast.success("Material removed");
    };
    // 2. دالة حذف أصل مرتبط
    const deleteWorkAsset = (idx) => {
        Swal.fire({
            title: 'Remove Asset?',
            text: "This asset will no longer be linked to this safety plan.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3c70a4',
            confirmButtonText: 'Yes, remove it'
        }).then((result) => {
            if (result.isConfirmed) {
                const newAssets = data.work_assets.filter((_, i) => i !== idx);
                setData({ ...data, work_assets: newAssets });
                toast.success("Asset unlinked");
            }
        });
    };
  const handleSave = async () => {
    try {
      setLoading(true);
      const res = await axios.post('/api/safety-plans/save', data);
      toast.success("Safety Plan Saved!");
      if (isNew) navigate(`/safety/${res.data.safetypnum}`);
    } catch (err) { toast.error("Save failed"); } 
    finally { setLoading(false); }
  };

    const handleDelete = () => {
        Swal.fire({
            title: 'Are you sure?', text: "This will delete the safety plan permanently!",
            icon: 'warning', showCancelButton: true, confirmButtonColor: '#d33', cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`/api/safety-plans/${data.safetypnum}`);
                    toast.success("Safety Plan deleted!");
                    navigate('/safety');
                } catch (err) { toast.error("Delete failed"); }
            }
        });
    };



  const handleLookupSelect = async (row) => {
    const newData = { ...data };
    const { field, targetIdx, subIdx } = lookup;
    
    if (field === 'hazardnum') {
      newData.hazards[targetIdx].hazardnum = row.hazardnum;
      newData.hazards[targetIdx].hazardtype = row.hazardtype;
      newData.hazards[targetIdx].description = row.description;
      try {
        const res = await axios.get(`/api/hazards/${row.hazardnum}/precautions`);
        if (res.data && res.data.length > 0) {
          // بنضيف الاحتياطات اللي جت من الماستر للسطر ده بالذات
          newData.hazards[targetIdx].precautions = res.data;
          toast.success(`Auto-filled ${res.data.length} precautions for this hazard`);
        }
      } catch (err) {
        console.error("Auto-fill failed", err);
      }
    } else if (field === 'precautionnum') {
      newData.hazards[targetIdx].precautions[subIdx].precautionnum = row.precautionnum;
      newData.hazards[targetIdx].precautions[subIdx].description = row.description;
    } else if (field === 'tagoutnum') {
      newData.tagouts[targetIdx].tagoutnum = row.tagoutnum;
      newData.tagouts[targetIdx].description = row.description;
      newData.tagouts[targetIdx].required_state = row.required_state;
    } else if (field === 'hazmat_item') {
      newData.hazmat[targetIdx].itemnum = row.itemnum || row.value;
      newData.hazmat[targetIdx].description = row.description;
    } else if (field === 'work_asset_row') {
      // بنجهز السجل الجديد
      const newAssetLink = {
        assetnum: row.assetnum || null,
        location: row.location || null,
        description: row.description || row.asset_desc || '',
        siteid: row.siteid || data.siteid || '',
        orgid: row.orgid || data.orgid || ''
      };

      // لو المصفوفة مش موجودة بنعرفها
      if (!newData.work_assets) newData.work_assets = [];
      
      // بنضيف السجل في المصفوفة
      newData.work_assets.push(newAssetLink);
      toast.success("Asset linked to plan");
    } else {
      newData[field] = row.value || row.site_id || row.org_id || row.assetnum || row.location_id;
    }
    setData(newData);
    setLookup({ ...lookup, isOpen: false });
  };
    // --- Global Handlers for Save/Delete (تقدر تشغلهم من أي مكان في التطبيق) ---  
    useEffect(() => {
        const triggerSave = () => handleSave();
        window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
        return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    }, [handleSave]);
    
    useEffect(() => {
        const triggerDelete = () => handleDelete();
        window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
        return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    }, [handleDelete]);

  return (
    <div className="flex flex-col h-screen bg-[#f3f4f6] text-[11px] font-sans uppercase">
      <UniversalLookup {...lookup} onClose={() => setLookup({...lookup, isOpen: false})} onSelect={handleLookupSelect} />
      
      {showStatusModal && <StatusModal currentStatus={data.status} recordId={data.safetypnum} appName="SAFETY" onClose={() => setShowStatusModal(false)} />}
      {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="SAFETY" recordId={data.safetypnum} onClose={() => setShowHistoryModal(false)} />}
      {!isNew && <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false); fetchCount();}} ownertable="SAFETYPLAN" ownerid={data.safetypnum} />}

      {/* Header Bar - PO Style */}
      <div className="bg-white p-2 border-b flex justify-between items-center shadow-sm z-20 font-bold tracking-tighter uppercase">
        <div className="flex gap-6 ml-4">
          <button onClick={() => navigate('/safety')} className="p-1 hover:bg-gray-100 rounded-full text-blue-900"><ArrowLeft size={18} /></button>
          <HeaderField label="Plan Num" value={data.safetypnum} isBold width="w-32" disabled={!isNew} onChange={v => setData({...data, safetypnum: v})} />
          <HeaderField label="Description" value={data.description} onChange={v => setData({...data, description: v})} width="w-[300px]" />
          <HeaderField label="Status" value={data.status} disabled width="w-24" />
          <HeaderField label="Site" value={data.siteid} disabled width="w-24" />
        </div>
        <div className="flex gap-2 mr-4">
            <button onClick={() => setShowStatusModal(true)} className="p-2 text-blue-900 hover:bg-blue-50 rounded"><Tag size={16}/></button>
            {!isNew && (
              <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 text-blue-900 rounded-sm hover:bg-blue-600 hover:text-white transition-all">
                <Paperclip size={18} />
                {count > 0 && <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center border border-white">{count}</span>}
              </button>
            )}
            <button onClick={() => setShowHistoryModal(true)} className="p-2 text-blue-900 hover:bg-blue-50 rounded"><History size={16}/></button>
            <button onClick={handleSave} disabled={loading} className="bg-[#3c70a4] text-white px-8 py-2 rounded font-black flex items-center gap-2 hover:bg-[#1a3353] shadow-lg transition-all">
               {loading ? 'Saving...' : <><Save size={14} /> SAVE</>}
            </button>
        </div>
      </div>

      {/* Tabs Menu - Blue Style */}
      <div className="flex bg-[#1a3353] text-white shadow-md">
        {[
          { id: 'Plan', label: 'Safety Plan', icon: <Info size={14}/> },
          { id: 'Hazards', label: 'Hazards & Precautions', icon: <Shield size={14}/> },
          { id: 'Hazmat', label: 'Hazardous Materials', icon: <FlaskConical size={14}/> },
          { id: 'LOTO', label: 'Lock Out / Tag Out', icon: <Zap size={14}/> },
          { id: 'Assets', label: 'Work Assets', icon: <List size={14} /> }
        ].map(t => (
          <button key={t.id} onClick={() => { setActiveTab(t.id); setSelectedHazardIdx(null); setSelectedLotoIdx(null); setSelectedHazmatIdx(null); }} className={`px-10 py-3 flex items-center gap-2 font-black border-r border-white/5 transition-all ${activeTab === t.id ? 'bg-white text-[#1a3353]' : 'opacity-75 hover:opacity-100'}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-[#f8f9fb]">
        {/* --- Tab 1: General Plan --- */}
        {activeTab === 'Plan' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto animate-in fade-in duration-500">
            <div className="col-span-8 space-y-6">
              <SectionCard title="Plan Information">
                <div className="grid grid-cols-2 gap-8">
                  <EditableField label="Plan Number" value={data.safetypnum} disabled={!isNew} />
                  <EditableField label="Organization" value={data.orgid} disabled />
                  <div className="col-span-2">
                    <EditableField label="Detailed Description" value={data.description} onChange={v => setData({...data, description: v})} />
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        )}

        {/* --- Tab 2: Hazards & Precautions --- */}
        {activeTab === 'Hazards' && (
        <div className="max-w-7xl mx-auto space-y-6 animate-in slide-in-from-bottom duration-500">
            <SectionCard title="Linked Hazards">
            <div className="bg-white border rounded shadow-sm overflow-hidden">
                <table className="w-full text-left">
                <thead className="bg-[#f9fafb] border-b text-gray-400 font-bold uppercase text-[9px]">
                    <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 border-r">Hazard Num</th>
                    <th className="p-2 border-r">Hazard Description</th>
                    <th className="p-2 border-r text-center w-32">Priority</th>
                    <th className="p-2 text-center w-12">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {data.hazards.map((h, i) => (
                    <tr key={i} onClick={() => setSelectedHazardIdx(i)} 
                        className={`border-b hover:bg-blue-50 cursor-pointer transition-colors ${selectedHazardIdx === i ? 'bg-[#e8f0fe] border-l-4 border-l-blue-800' : ''}`}>
                        <td className="p-2 text-blue-600">{selectedHazardIdx === i && <ChevronRight size={14} className="animate-pulse"/>}</td>
                        <td className="p-2 border-r font-black text-blue-900 uppercase">{h.hazardnum || 'NEW_HAZARD'}</td>
                        <td className="p-2 border-r font-semibold text-gray-600">{h.description}</td>
                        <td className="p-2 border-r text-center uppercase font-black text-orange-600">{h.priority || 'MEDIUM'}</td>
                        <td className="p-2 text-center text-red-500 hover:bg-red-50 cursor-pointer" 
                            onClick={(e) => { e.stopPropagation(); deleteHazard(i); }}>
                            <Trash2 size={12}/>
                        </td>
                    </tr>
                    ))}
                </tbody>
                </table>
                <button onClick={addHazard} className="m-2 bg-slate-100 text-slate-600 hover:bg-blue-800 hover:text-white px-4 py-1 rounded-sm font-black flex items-center gap-2 transition-all">
                <Plus size={12}/> NEW HAZARD LINE
                </button>
            </div>
            </SectionCard>

            {selectedHazardIdx !== null && data.hazards[selectedHazardIdx] && (
            <div className="grid grid-cols-12 gap-6 animate-in zoom-in-95 duration-300">
                <div className="col-span-4">
                <SectionCard title={`Hazard Details: ${data.hazards[selectedHazardIdx].hazardnum || ''}`}>
                    <div className="space-y-4">
                    <EditableField label="Hazard Num" value={data.hazards[selectedHazardIdx].hazardnum} 
                        onSearch={() => setLookup({isOpen: true, title: 'Select Hazard Master', type: 'hazards', field: 'hazardnum', targetIdx: selectedHazardIdx})} />
                    <EditableField label="Hazard Type" value={data.hazards[selectedHazardIdx].hazardtype} disabled />
                    <div className="pt-2">
                        <label className="text-gray-400 font-black text-[8px] uppercase tracking-widest italic">Hazard Statement</label>
                        <textarea className="w-full mt-1 p-2 bg-gray-50 border rounded outline-none font-bold text-blue-900 text-[10px]" rows="3"
                        value={data.hazards[selectedHazardIdx].description} onChange={(e) => {
                            let newH = [...data.hazards]; newH[selectedHazardIdx].description = e.target.value; setData({...data, hazards: newH});
                        }} />
                    </div>
                    </div>
                </SectionCard>
                </div>

                <div className="col-span-8">
                <SectionCard title={`Precautions for ${data.hazards[selectedHazardIdx].hazardnum || 'this hazard'}`}>
                    <div className="bg-white border rounded shadow-sm overflow-hidden text-[10px]">
                    <table className="w-full text-left">
                        <thead className="bg-[#f0f4f8] border-b text-slate-500 font-black uppercase text-[8px]">
                        <tr>
                            <th className="p-2 border-r w-32">Precaution Num</th>
                            <th className="p-2 border-r">Instruction / Description</th>
                            <th className="p-2 w-12 text-center">Delete</th>
                        </tr>
                        </thead>
                        <tbody>
                        {(data.hazards[selectedHazardIdx].precautions || []).map((p, pi) => (
                            <tr key={pi} className="border-b">
                            <td className="p-2 border-r font-black text-blue-800 uppercase group">
                                <div className="flex justify-between items-center">
                                    {p.precautionnum || '---'}
                                    <Search size={10} className="text-blue-400 cursor-pointer" 
                                    onClick={() => setLookup({isOpen: true, title: 'Select Precaution', type: 'precautions', field: 'precautionnum', targetIdx: selectedHazardIdx, subIdx: pi})}/>
                                </div>
                            </td>
                            <td className="p-2 border-r font-medium text-gray-500">{p.description}</td>
                            <td className="p-2 text-center text-red-300 hover:text-red-600 cursor-pointer" 
                                onClick={() => deletePrecaution(selectedHazardIdx, pi)}>
                                <Trash2 size={10}/>
                            </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <button onClick={() => addPrecaution(selectedHazardIdx)} className="m-2 text-blue-800 font-black text-[9px] flex items-center gap-1 hover:underline">
                        <Plus size={10}/> ADD PRECAUTION
                    </button>
                    </div>
                </SectionCard>
                </div>
            </div>
            )}
        </div>
        )}

        {/* --- Tab 3: Hazardous Materials --- */}
        {activeTab === 'Hazmat' && (
            <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom duration-500 font-bold uppercase">
                <SectionCard title="Hazardous Materials (Hazmat)">
                    <div className="bg-white border rounded overflow-hidden">
                        <table className="w-full text-left text-[10px]">
                            <thead className="bg-[#f9fafb] border-b text-gray-400 font-bold uppercase text-[9px]">
                                <tr>
                                    <th className="p-2 w-8"></th>
                                    <th className="p-2 border-r">Item Num</th>
                                    <th className="p-2 border-r">Material Description</th>
                                    <th className="p-2 border-r text-center w-24">Health Rating</th>
                                    <th className="p-2 text-center w-12">Delete</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.hazmat.map((m, i) => (
                                    <tr key={i} onClick={() => setSelectedHazmatIdx(i)} className={`border-b hover:bg-blue-50 cursor-pointer ${selectedHazmatIdx === i ? 'bg-[#e8f0fe] border-l-4 border-l-blue-800' : ''}`}>
                                        <td className="p-2 text-blue-600">{selectedHazmatIdx === i && <ChevronRight size={14} />}</td>
                                        <td className="p-2 border-r text-blue-900 font-black">{m.itemnum || 'NEW'}</td>
                                        <td className="p-2 border-r font-semibold text-gray-600">{m.description}</td>
                                        <td className="p-2 border-r text-center text-red-600 font-black">{m.health_rating || '3'}</td>
                                        <td className="p-2 text-center" onClick={(e) => { e.stopPropagation(); deleteHazmat(i); }}>
                                            <Trash2 size={12} className="text-red-400 hover:text-red-700" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button onClick={addHazmatRow} className="m-2 bg-slate-100 text-slate-600 hover:bg-blue-800 hover:text-white px-4 py-1 rounded-sm font-black flex items-center gap-2">
                            <Plus size={12} /> ADD HAZMAT LINE
                        </button>
                    </div>
                </SectionCard>
                {selectedHazmatIdx !== null && data.hazmat[selectedHazmatIdx] && (
                    <div className="bg-white border p-6 grid grid-cols-4 gap-8 shadow-md rounded-sm border-t-4 border-t-blue-800 animate-in zoom-in-95">
                        <EditableField label="Material (Item Num)" value={data.hazmat[selectedHazmatIdx].itemnum} onSearch={() => setLookup({ isOpen: true, title: 'Hazmat Lookup', type: 'items', field: 'hazmat_item', targetIdx: selectedHazmatIdx })} />
                        <div className="col-span-2">
                            <EditableField label="Health Rating / Precautions" value={data.hazmat[selectedHazmatIdx].health_rating} onChange={v => {
                                const nm = [...data.hazmat]; nm[selectedHazmatIdx].health_rating = v; setData({ ...data, hazmat: nm });
                            }} />
                        </div>
                    </div>
                )}
            </div>
        )}

        {/* --- Tab 4: Lock Out / Tag Out (LOTO) --- */}
        {activeTab === 'LOTO' && (
          <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom duration-500">
             <div className="bg-white border rounded shadow-sm overflow-hidden text-[10px]">
                <table className="w-full text-left">
                  <thead className="bg-[#f9fafb] border-b text-gray-400 font-bold uppercase text-[9px]">
                    <tr>
                      <th className="p-2 w-8"></th>
                      <th className="p-2 border-r">Sequence</th>
                      <th className="p-2 border-r">Tag Out Num</th>
                      <th className="p-2 border-r">Description</th>
                      <th className="p-2">Req. State</th>
                      <th className="p-2 w-12 text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tagouts.map((t, i) => (
                      <tr key={i} onClick={() => setSelectedLotoIdx(i)} className={`border-b hover:bg-blue-50 cursor-pointer ${selectedLotoIdx === i ? 'bg-[#e8f0fe] border-l-4 border-l-blue-800' : ''}`}>
                         <td className="p-2 text-blue-600">{selectedLotoIdx === i && <ChevronRight size={12}/>}</td>
                         <td className="p-2 border-r font-bold text-center">{t.sequence}</td>
                         <td className="p-2 border-r font-black text-blue-900">{t.tagoutnum || 'NEW'}</td>
                         <td className="p-2 border-r font-semibold">{t.description}</td>
                         <td className="p-2 font-black text-red-600">{t.required_state}</td>
                         <td className="p-2 text-center text-red-500 hover:bg-red-50 cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); deleteLotoStep(i); }}>
                            <Trash2 size={12}/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             </div>
             {selectedLotoIdx !== null && data.tagouts[selectedLotoIdx] && (
               <div className="bg-white border p-6 grid grid-cols-4 gap-8 shadow-md rounded-sm border-t-4 border-t-blue-800 animate-in zoom-in-95">
                  <EditableField label="Tag Out Num" value={data.tagouts[selectedLotoIdx].tagoutnum} onSearch={() => setLookup({isOpen: true, title: 'Select TagOut', type: 'tagouts', field: 'tagoutnum', targetIdx: selectedLotoIdx})} />
                  <EditableField label="Sequence" type="number" value={data.tagouts[selectedLotoIdx].sequence} disabled />
                  <div className="col-span-2">
                    <EditableField label="LOTO Description" value={data.tagouts[selectedLotoIdx].description} onChange={v => {
                        const nl = [...data.tagouts]; nl[selectedLotoIdx].description = v; setData({...data, tagouts: nl});
                    }} />
                  </div>
               </div>
             )}
             <button onClick={addLotoStep} className="bg-[#3c70a4] text-white px-8 py-1.5 rounded-sm font-black flex items-center gap-2">
                <Plus size={14}/> Add LOTO Step
             </button>
          </div>
        )}
        {activeTab === 'Assets' && (
          <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom duration-500 font-bold uppercase">
            <SectionCard title="Work Assets (Assets & Locations Covered)">
              <div className="bg-white border rounded overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-[#f9fafb] border-b text-gray-400 font-bold uppercase text-[9px]">
                    <tr>
                      <th className="p-2 border-r w-40">Asset / Location</th>
                      <th className="p-2 border-r">Description</th>
                      <th className="p-2 border-r text-center w-24">Site</th>
                      <th className="p-2 border-r text-center w-24">Org</th>
                      <th className="p-2 text-center w-12">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.work_assets?.length > 0 ? (
                      data.work_assets.map((wa, i) => (
                        <tr key={i} className="border-b hover:bg-blue-50 cursor-pointer">
                          <td className="p-2 border-r font-black text-blue-900 uppercase">
                            {wa.assetnum || wa.location}
                          </td>
                          <td className="p-2 border-r font-semibold text-gray-500">
                            {wa.description}
                          </td>
                          <td className="p-2 border-r text-center">{wa.siteid || '-'}</td>
                          <td className="p-2 border-r text-center">{wa.orgid || '-'}</td>
                          <td className="p-2 text-center" onClick={(e) => { e.stopPropagation(); deleteWorkAsset(i); }}>
                             <Trash2 size={12} className="text-red-400 hover:text-red-700 cursor-pointer mx-auto" />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className="p-10 text-center text-gray-300 italic">
                          No assets or locations linked. This plan is currently generic.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="p-2 bg-gray-50 border-t">
                    <button onClick={addWorkAsset} className="bg-[#3c70a4] text-white px-6 py-1.5 rounded-sm font-black flex items-center gap-2 hover:bg-[#1a3353] transition-all shadow-sm">
                      <Plus size={14}/> ADD ASSET / LOCATION
                    </button>
                </div>
              </div>
            </SectionCard>
          </div>
        )}
      </div>
    </div>
  );
};

// المكونات الفرعية
const HeaderField = ({ label, value, onChange, width, isBold, disabled }) => (
  <div className="flex flex-col border-r px-4 last:border-0">
    <label className="text-[8px] text-gray-400 font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
    <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-blue-900 text-[12px]' : 'text-blue-700'} bg-transparent focus:border-blue-500`} 
           value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-white border rounded shadow-sm overflow-hidden transition-all hover:shadow-md">
    <div className="p-2 bg-slate-50 border-b font-black text-slate-500 uppercase text-[9px] tracking-widest flex items-center gap-2">
      <div className="w-1.5 h-3 bg-blue-800 rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled }) => (
  <div className="flex flex-col gap-1 border-b border-gray-200 group focus-within:border-blue-500 transition-all py-1">
    <label className="text-gray-400 font-black text-[8px] uppercase tracking-widest italic">{label}</label>
    <div className="flex items-center justify-between pb-0.5">
      <input type={type} disabled={disabled} className="outline-none font-black text-blue-900 bg-transparent w-full text-[11px]" 
        value={value || ''} onChange={e => onChange && onChange(e.target.value)} />
      {onSearch && <Search size={12} className="text-blue-400 cursor-pointer hover:text-blue-800" onClick={onSearch} />}
    </div>
  </div>
);

export default SafetyPlanDetails;